import { AccountFeature, isAccountFeatureEnabled } from 'hooks/use-account-feature';
import { isTauriApp } from 'utils/is-tauri';
/** Tab-local shell preference (sessionStorage only). */ export const APP_SHELL_MODE_STORAGE_KEY = 'app-shell-mode';
export const DEFAULT_APP_SHELL_MODE = 'rocketlane';
const isAppShellMode = (value)=>value === 'rocketlane' || value === 'nitro-claw';
export const isNitroClawPathname = (pathname)=>{
    const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
    return normalized === '/nitro-claw' || normalized.startsWith('/nitro-claw/');
};
const getSessionStorage = ()=>{
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        return window.sessionStorage;
    } catch (unused) {
        return null;
    }
};
/**
 * When sessionStorage is blocked (private mode / quota / policy), keep the
 * resolved mode in-memory so getAppShellMode / isNitroClawShell stay aligned
 * with the provider after resolveAndPin / persist.
 */ let memoryMode = null;
const readMode = (storage, key)=>{
    if (storage == null) {
        return null;
    }
    try {
        const raw = storage.getItem(key);
        if (raw == null) {
            return null;
        }
        const parsed = JSON.parse(raw);
        return isAppShellMode(parsed) ? parsed : null;
    } catch (unused) {
        return null;
    }
};
const writeMode = (storage, key, mode)=>{
    if (storage == null) {
        return;
    }
    try {
        storage.setItem(key, JSON.stringify(mode));
    } catch (unused) {}
};
export const getAppShellMode = ()=>{
    var _ref;
    // Memory wins over session so a failed/stale write can't fight this tab's
    // last explicit switch until the next full load (boot reads session first).
    return (_ref = memoryMode !== null && memoryMode !== void 0 ? memoryMode : readMode(getSessionStorage(), APP_SHELL_MODE_STORAGE_KEY)) !== null && _ref !== void 0 ? _ref : DEFAULT_APP_SHELL_MODE;
};
export const persistAppShellMode = (mode)=>{
    memoryMode = mode;
    writeMode(getSessionStorage(), APP_SHELL_MODE_STORAGE_KEY, mode);
};
/** Clears the in-memory fallback between unit tests. */ export const resetAppShellModeMemoryForTests = ()=>{
    memoryMode = null;
};
const tryReadOpenerSessionMode = ()=>{
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        const opener = window.opener;
        if (opener == null || opener.closed || opener.location.origin !== window.location.origin) {
            return null;
        }
        return readMode(opener.sessionStorage, APP_SHELL_MODE_STORAGE_KEY);
    } catch (unused) {
        return null;
    }
};
/**
 * Boot pin: session → opener session → claw URL forces claw → else rocketlane.
 */ export const resolveAndPinAppShellModeSync = (pathname = typeof window !== 'undefined' ? window.location.pathname : '/')=>{
    if (typeof window === 'undefined') {
        return DEFAULT_APP_SHELL_MODE;
    }
    try {
        var _readMode;
        const onClawUrl = isNitroClawPathname(pathname);
        const session = getSessionStorage();
        const pin = (mode)=>{
            memoryMode = mode;
            writeMode(session, APP_SHELL_MODE_STORAGE_KEY, mode);
            return mode;
        };
        const existing = (_readMode = readMode(session, APP_SHELL_MODE_STORAGE_KEY)) !== null && _readMode !== void 0 ? _readMode : memoryMode;
        if (existing != null) {
            return pin(onClawUrl ? 'nitro-claw' : existing);
        }
        const fromOpener = tryReadOpenerSessionMode();
        if (fromOpener != null) {
            return pin(onClawUrl ? 'nitro-claw' : fromOpener);
        }
        return pin(onClawUrl ? 'nitro-claw' : DEFAULT_APP_SHELL_MODE);
    } catch (unused) {
        return memoryMode !== null && memoryMode !== void 0 ? memoryMode : DEFAULT_APP_SHELL_MODE;
    }
};
export const isNitroClawShell = ()=>{
    if (!isAccountFeatureEnabled(AccountFeature.NitroClaw)) {
        return false;
    }
    return isTauriApp() || getAppShellMode() === 'nitro-claw';
};
